import type {
  DetectionBox,
  DetectionCategory,
  DetectionSource,
  LocalAiContextInput,
  LocalAiHealth,
  LocalAiModel,
  LocalAiProfile,
  LocalAiProvider,
  LocalAiReview,
  LocalAiReviewDecision,
  LocalAiReviewInput,
  LocalAiVlmDiagnostics,
  LocalAiVlmMainResponse,
  LocalAiVlmMainInput,
  LocalAiVisionInput,
  PresetConfig,
  Rect
} from "@aimosaic/core";
import { clampBoxToImage, dedupeBoxes } from "@aimosaic/core";

const KNOWN_CATEGORIES = new Set<DetectionCategory>([
  "ip",
  "url",
  "email",
  "token",
  "secret",
  "resident-id",
  "phone",
  "face",
  "logo",
  "manual"
]);

interface GeminiDetectionRequest {
  apiKey: string;
  imageDataUrl: string;
  width: number;
  height: number;
  preset: PresetConfig;
  fetchImpl?: typeof fetch;
}

interface LocalAiProviderOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

interface LocalAiDetectionResponse {
  boxes?: unknown;
  diagnostics?: unknown;
}

interface LocalAiReviewResponse {
  reviews?: unknown;
}

export async function detectWithGemini({
  apiKey,
  imageDataUrl,
  width,
  height,
  preset,
  fetchImpl = fetch
}: GeminiDetectionRequest): Promise<DetectionBox[]> {
  const image = parseDataUrl(imageDataUrl);
  const prompt = [
    "Find sensitive regions in this image for privacy redaction.",
    "Return only JSON. Use categories from this list:",
    preset.categories.join(", "),
    "Each item must be {category,bbox,confidence,text}.",
    "bbox may be [x,y,width,height] in pixels or normalized 0-1 values.",
    "Do not include explanations."
  ].join("\n");

  const response = await fetchImpl(
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent?key=${encodeURIComponent(apiKey)}`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        contents: [
          {
            role: "user",
            parts: [
              { text: prompt },
              {
                inline_data: {
                  mime_type: image.mimeType,
                  data: image.base64
                }
              }
            ]
          }
        ],
        generationConfig: {
          responseMimeType: "application/json"
        }
      })
    }
  );

  if (!response.ok) {
    throw new Error(`Gemini request failed with status ${response.status}`);
  }

  const payload = (await response.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
  };
  const text = payload.candidates?.[0]?.content?.parts?.map((part) => part.text ?? "").join("\n") ?? "";
  return parseGeminiDetectionText(text, width, height);
}

export function createLocalAiProvider({
  baseUrl,
  fetchImpl = fetch
}: LocalAiProviderOptions): LocalAiProvider {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");

  return {
    async health(): Promise<LocalAiHealth> {
      const response = await fetchImpl(`${normalizedBaseUrl}/health`);
      if (!response.ok) {
        return { ok: false, runtime: "unknown", message: `HTTP ${response.status}` };
      }
      return normalizeLocalAiHealth(await response.json());
    },
    async models(): Promise<LocalAiModel[]> {
      const response = await fetchImpl(`${normalizedBaseUrl}/models`);
      if (!response.ok) return [];
      const payload = (await response.json()) as { models?: unknown };
      return Array.isArray(payload.models) ? payload.models.flatMap(normalizeLocalAiModel) : [];
    },
    async detectContext(input: LocalAiContextInput): Promise<DetectionBox[]> {
      const payload = await postLocalAiDetection(fetchImpl, `${normalizedBaseUrl}/detect/context`, input);
      return parseLocalAiDetectionResponse(payload, "local-llm", input.image.width, input.image.height);
    },
    async detectVision(input: LocalAiVisionInput): Promise<DetectionBox[]> {
      const payload = await postLocalAiDetection(fetchImpl, `${normalizedBaseUrl}/detect/vision`, input);
      return parseLocalAiDetectionResponse(payload, "local-vlm", input.image.width, input.image.height);
    },
    async detectVlmMain(input: LocalAiVlmMainInput): Promise<LocalAiVlmMainResponse> {
      const payload = await postLocalAiDetection(fetchImpl, `${normalizedBaseUrl}/detect/vlm-main`, input);
      return {
        boxes: parseLocalAiDetectionResponse(payload, "local-vlm", input.image.width, input.image.height),
        diagnostics: normalizeLocalAiVlmDiagnostics(payload.diagnostics)
      };
    },
    async reviewBoxes(input: LocalAiReviewInput): Promise<LocalAiReview[]> {
      const payload = await postLocalAiJson<LocalAiReviewResponse>(
        fetchImpl,
        `${normalizedBaseUrl}/detect/review`,
        input
      );
      return parseLocalAiReviewResponse(payload);
    }
  };
}

function normalizeLocalAiVlmDiagnostics(value: unknown): LocalAiVlmDiagnostics | undefined {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  return {
    model: typeof record.model === "string" ? record.model : undefined,
    httpStatus: integerOrUndefined(record.httpStatus),
    schemaMode: typeof record.schemaMode === "boolean" ? record.schemaMode : undefined,
    rawResponseExcerpt: typeof record.rawResponseExcerpt === "string" ? record.rawResponseExcerpt : undefined,
    acceptedCount: countOrZero(record.acceptedCount),
    rejectedCount: countOrZero(record.rejectedCount),
    invalidCategoryCount: countOrZero(record.invalidCategoryCount),
    placeholderRejectedCount: countOrZero(record.placeholderRejectedCount),
    parseErrorCount: countOrZero(record.parseErrorCount)
  };
}

function integerOrUndefined(value: unknown): number | undefined {
  const number = Number(value);
  return Number.isInteger(number) ? number : undefined;
}

function countOrZero(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.round(number) : 0;
}

export function parseGeminiDetectionText(text: string, imageWidth: number, imageHeight: number): DetectionBox[] {
  const parsed = parseJsonArray(stripCodeFence(text));
  if (!parsed) return [];

  const boxes: DetectionBox[] = [];
  for (const item of parsed) {
    const category = typeof item.category === "string" ? item.category : "";
    if (!KNOWN_CATEGORIES.has(category as DetectionCategory)) continue;

    const bbox = parseBox(item.bbox, imageWidth, imageHeight);
    if (!bbox || bbox.width <= 0 || bbox.height <= 0) continue;

    boxes.push({
      id: `gemini-${category}-${boxes.length}`,
      category: category as DetectionCategory,
      bbox,
      confidence: normalizeConfidence(item.confidence),
      source: "gemini",
      text: typeof item.text === "string" ? item.text : undefined,
      action: "mask"
    });
  }

  return dedupeBoxes(boxes);
}

export function parseLocalAiDetectionResponse(
  payload: LocalAiDetectionResponse,
  source: Extract<DetectionSource, "local-llm" | "local-vlm">,
  imageWidth: number,
  imageHeight: number
): DetectionBox[] {
  if (!Array.isArray(payload.boxes)) return [];

  const boxes: DetectionBox[] = [];
  for (const item of payload.boxes) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const category = typeof record.category === "string" ? record.category : "";
    if (!KNOWN_CATEGORIES.has(category as DetectionCategory)) continue;

    const bbox = parseBox(record.bbox, imageWidth, imageHeight);
    if (!bbox || bbox.width <= 0 || bbox.height <= 0) continue;

    boxes.push({
      id: `${source}-${category}-${boxes.length}`,
      category: category as DetectionCategory,
      bbox,
      confidence: normalizeConfidence(record.confidence),
      source,
      text: typeof record.text === "string" ? record.text : undefined,
      reason: typeof record.reason === "string" ? record.reason : undefined,
      evidenceText: typeof record.evidenceText === "string" ? record.evidenceText : undefined,
      requiresReview: Boolean(record.requiresReview),
      action: "mask"
    });
  }

  return dedupeBoxes(boxes);
}

async function postLocalAiDetection(
  fetchImpl: typeof fetch,
  url: string,
  input: LocalAiContextInput | LocalAiVisionInput | LocalAiVlmMainInput
): Promise<LocalAiDetectionResponse> {
  return postLocalAiJson<LocalAiDetectionResponse>(fetchImpl, url, input);
}

async function postLocalAiJson<T>(
  fetchImpl: typeof fetch,
  url: string,
  input: LocalAiContextInput | LocalAiVisionInput | LocalAiVlmMainInput | LocalAiReviewInput
): Promise<T> {
  const response = await fetchImpl(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    const message = text.trim() ? `: ${text.trim().slice(0, 200)}` : "";
    throw new Error(`Local AI request failed with status ${response.status}${message}`);
  }
  return (await response.json()) as T;
}

export function parseLocalAiReviewResponse(payload: LocalAiReviewResponse): LocalAiReview[] {
  if (!Array.isArray(payload.reviews)) return [];
  return payload.reviews.flatMap(normalizeLocalAiReview);
}

function normalizeLocalAiReview(value: unknown): LocalAiReview[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const boxId = reviewString(record.boxId) ?? reviewString(record.box_id) ?? reviewString(record.id) ?? "";
  const decision = parseReviewDecision(record.decision);
  if (!boxId || !decision) return [];
  const category = typeof record.category === "string" && KNOWN_CATEGORIES.has(record.category as DetectionCategory)
    ? (record.category as DetectionCategory)
    : undefined;
  if (decision === "change" && !category) return [];
  return [
    {
      boxId,
      decision,
      category,
      confidence: confidenceOrUndefined(record.confidence),
      reason: typeof record.reason === "string" && record.reason.trim() ? record.reason.trim() : undefined,
      requiresReview: typeof record.requiresReview === "boolean" ? record.requiresReview : undefined
    }
  ];
}

function parseReviewDecision(value: unknown): LocalAiReviewDecision | null {
  if (value === "keep" || value === "drop" || value === "change") return value;
  if (typeof value !== "string") return null;
  const normalized = value.toLowerCase().trim();
  if (["remove", "exclude", "ignore", "delete", "false_positive"].includes(normalized)) return "drop";
  if (["mask", "redact", "sensitive", "valid"].includes(normalized)) return "keep";
  if (["reclassify", "recategorize"].includes(normalized)) return "change";
  return null;
}

function confidenceOrUndefined(value: unknown): number | undefined {
  const number = Number(value);
  if (!Number.isFinite(number)) return undefined;
  return Math.min(Math.max(number, 0), 1);
}

function reviewString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function normalizeLocalAiHealth(value: unknown): LocalAiHealth {
  if (!value || typeof value !== "object") {
    return { ok: false, runtime: "unknown" };
  }
  const record = value as Record<string, unknown>;
  return {
    ok: Boolean(record.ok),
    runtime: parseLocalAiRuntime(record.runtime),
    version: typeof record.version === "string" ? record.version : undefined,
    modelsReady: typeof record.modelsReady === "boolean" ? record.modelsReady : undefined,
    visionReady: typeof record.visionReady === "boolean" ? record.visionReady : undefined,
    vlmReady: typeof record.vlmReady === "boolean" ? record.vlmReady : undefined,
    profiles: Array.isArray(record.profiles) ? record.profiles.flatMap(parseLocalAiProfile) : undefined,
    message: typeof record.message === "string" ? record.message : undefined
  };
}

function normalizeLocalAiModel(value: unknown): LocalAiModel[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const id = typeof record.id === "string" ? record.id : "";
  const kind = record.kind === "text" || record.kind === "vision" ? record.kind : null;
  if (!id || !kind) return [];
  return [
    {
      id,
      kind,
      profile: parseLocalAiProfile(record.profile)[0],
      displayName: typeof record.displayName === "string" ? record.displayName : undefined
    }
  ];
}

function parseLocalAiRuntime(value: unknown): LocalAiHealth["runtime"] {
  if (
    value === "tauri-sidecar" ||
    value === "docker" ||
    value === "ollama-compatible" ||
    value === "llama.cpp"
  ) {
    return value;
  }
  return "unknown";
}

function parseLocalAiProfile(value: unknown): LocalAiProfile[] {
  return value === "fast" || value === "precise" ? [value] : [];
}

function parseDataUrl(dataUrl: string): { mimeType: string; base64: string } {
  const match = /^data:([^;]+);base64,(.+)$/s.exec(dataUrl);
  if (!match) {
    throw new Error("imageDataUrl must be a base64 data URL");
  }
  return { mimeType: match[1] ?? "image/png", base64: match[2] ?? "" };
}

function stripCodeFence(text: string): string {
  const trimmed = text.trim();
  const fence = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(trimmed);
  return fence?.[1]?.trim() ?? trimmed;
}

function parseJsonArray(text: string): Array<Record<string, unknown>> | null {
  try {
    const value = JSON.parse(text) as unknown;
    return Array.isArray(value) ? (value as Array<Record<string, unknown>>) : null;
  } catch {
    return null;
  }
}

function parseBox(value: unknown, imageWidth: number, imageHeight: number): Rect | null {
  const numbers = Array.isArray(value)
    ? value.map((part) => Number(part))
    : value && typeof value === "object"
      ? [
          Number((value as Record<string, unknown>).x),
          Number((value as Record<string, unknown>).y),
          Number((value as Record<string, unknown>).width ?? (value as Record<string, unknown>).w),
          Number((value as Record<string, unknown>).height ?? (value as Record<string, unknown>).h)
        ]
      : [];
  if (numbers.length !== 4) return null;
  if (numbers.some((part) => !Number.isFinite(part))) return null;

  const [x, y, width, height] = numbers;
  const normalized = numbers.every((part) => part >= 0 && part <= 1);
  const rect = normalized
    ? {
        x: x * imageWidth,
        y: y * imageHeight,
        width: width * imageWidth,
        height: height * imageHeight
      }
    : { x, y, width, height };

  return clampBoxToImage(roundRect(rect), imageWidth, imageHeight);
}

function roundRect(rect: Rect): Rect {
  return {
    x: Math.round(rect.x),
    y: Math.round(rect.y),
    width: Math.round(rect.width),
    height: Math.round(rect.height)
  };
}

function normalizeConfidence(value: unknown): number {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0.5;
  return Math.min(Math.max(number, 0), 1);
}
